<template>
	<div class="your-profile">
		<!-- BIG SLOGAN -->
		<div class="container">
			<h1 class="mb-30">{{ $t("profile.edit.header") }}</h1>
		</div><!-- .container -->

		<!-- PROFILE -->
		<form @submit.prevent="submit">
			<div class="container mb-30 profile">
				<div class="row">
					<div class="col-md-4">
						<div class="profile-text">
							<input v-model="user.city" class="form-control mb-20" :placeholder="$t('city')">
							<select v-if="countries.length" v-model="user.country_id" class="form-control mb-20">
								<option disabled value="">{{ $t('country') }}</option>
								<option v-for="country in countries" :value="country.id">{{ country.name }}</option>
							</select>
              <select v-if="countries.length" v-model="user.country_of_residence_id" class="form-control mb-20">
                <option disabled value="">{{ $t('countryOfResidence') }}</option>
                <option v-for="country in countries" :value="country.id">{{ country.name }}</option>
              </select>
							<textarea v-model="user.short_description" class="form-control my-short-desc" rows="6" maxlength="150" :placeholder="$t('profile.edit.shortDescription')"></textarea>
						</div><!-- .profile-text -->
					</div><!-- .col-md-4 -->
					<div class="col-md-4">
						<div class="profile-photo">
              <div v-if="image">
                <img :src="image" />
                <button type="button" @click="removeImage" class="btn btn-default btn-lg button-wide-crowd mb-0">{{ $t("cancel") }}</button>
              </div>
              <img v-else-if="user_photo" :src="user_photo" />
						</div><!-- .profile-photo -->
					</div><!-- .col-md-4 -->

					<div class="col-md-4">
            <!-- User KYC documents -->
            <user-kyc :documentId="user.kyc_document_id" @submitted="setKycIds"/>

            <div class="profile-right">
							<h3>{{ $t("profile.edit.photo") }}</h3>
							<p>{{ $t("profile.edit.photoInfo") }}</p>
              <button type="button" @click="$refs.addDocuments.$refs.input.click()" class="btn btn-default btn-lg button-wide-crowd mb-0 uppercase">
                <span v-if="!image && !user_photo">{{ $t("profile.edit.addImageButton") }}</span>
                <span v-else>{{ $t("profile.edit.changeImageButton") }}</span>
              </button>
              <upload-base64 :multiple="false" :done="getFile" ref="addDocuments"></upload-base64>
              <div v-if="sizeError" class="has-error">
                <p class="help-block pt-5 mb-0">{{ $t("profile.edit.photoSizeError") }}</p>
              </div>
						</div><!-- .profile-right -->
					</div><!-- .col-md-4 -->

				</div><!-- .row -->
			</div><!-- .container mb-30 profile -->

			<!-- PROFILE DETAILS -->
			<div class="container profile-details crowd" id="scrollTo">
				<!-- Profile, Campaigns, Contributions, Comments tabs -->
				<div class="row">
					<div class="col-md-9">
						<!-- Nav tabs -->
						<ul class="nav nav-tabs" role="tablist" id="usertabs">
							<li role="presentation" class="active">
                <a class="first" href="#profile-campaigns" aria-controls="profile-campaigns" role="tab" data-toggle="tab" ref="campaigns" @click="changeQueryTab('campaigns')">
                  {{ $t("profile.edit.campaigns") + ': ' + projects.length }}
                </a>
              </li>
							<li role="presentation">
                <a href="#profile-contributions" aria-controls="profile-contributions" role="tab" data-toggle="tab" @click="changeQueryTab('contributions')">
                  {{ $t("profile.edit.contributions") + ': ' + contributions.length }}
                </a>
              </li>
							<li role="presentation">
                <a href="#profile-comments" aria-controls="profile-comments" role="tab" data-toggle="tab" @click="changeQueryTab('comments')">
                  {{ $t("profile.edit.comments") + ': ' + comments.length }}
                </a>
              </li>
              <li role="presentation">
                <a href="#profile-delivery-address" aria-controls="profile-delivery-address" role="tab" data-toggle="tab" ref="delivery-address" @click="changeQueryTab('delivery-address')">
                  {{ $t("deliveryAddress.deliveryAddress") }}
                </a>
              </li>
							<li role="presentation">
                <a href="#profile-about" aria-controls="profile-about" role="tab" data-toggle="tab" ref="about" @click="changeQueryTab('about')">
                  {{ $t("profile.edit.aboutMe") }}
                </a>
              </li>
              <li role="presentation">
                <a href="#profile-organization" aria-controls="profile-organization" role="tab" data-toggle="tab" ref="organization" @click="changeQueryTab('organization')">
                  {{ $t("organization.organization") }}
                </a>
              </li>
            </ul>
						<!-- Tab panes -->
						<div class="tab-content">
							<!-- Profile -->
							<div role="tabpanel" class="tab-pane fade" id="profile-about">
                <h3 class="mb-20">Email: <h2 style="display: inline">{{ user.email }}</h2></h3>
                <form-error :errors='errors' :attribute="'full_name'">
								  <input v-model="user.full_name" class="form-control mb-20" :placeholder="$t('profile.edit.firstName')">
                </form-error>

                <datepicker v-model="user.birthday"
                  format="yyyy-MM-dd"
                  :typeable="false"
                  :language="datapickerLang"
                  class="mb-20"
                  input-class="form-control"
                  :placeholder="$t('profile.edit.birthday')">
                </datepicker>

                <input v-model="user.phone" class="form-control mb-20" :placeholder="$t('profile.edit.phone')">
                <div class="row">
                  <div class="col-md-5">
                    <input v-model="user.address_line_1" class="form-control" :placeholder="$t('profile.edit.streetName')">
                  </div>
                  <div class="col-md-3">
                    <input v-model="user.address_line_2" class="form-control" :placeholder="$t('profile.edit.streetNumber')">
                  </div>
                  <div class="col-md-4">
                    <input v-model="user.zip_code" class="form-control" :placeholder="$t('profile.edit.zipCode')">
                  </div>
                </div>
                <trumbowyg v-if="user.id"
                  :content="user.description"
                  @tbwblur="setDescription"
                  class="form-control"
                  rows="10"
                  :placeholder="$t('profile.edit.quiteLongDescription')">
                </trumbowyg>
								<h3>{{ $t("profile.edit.specialistTitleOne") }}
									<br>
									<small>{{ $t("profile.edit.specialistTitleTwo") }}</small>
								</h3>
								<select v-if="professions.length" v-model="user.profession_id" class="form-control mb-20">
                  <option disabled value="">{{ $t('profession') }}</option>
									<option v-for="profession in professions" :value="profession.id">{{ profession.name }}</option>
								</select>
								<div class="all-categories mb-20">
									<strong>{{ $t("profile.edit.updatePassword") }}</strong>
								</div>
								<input v-model="user.password"
                  type="password"
                  class="form-control mb-20"
                  :placeholder="$t('profile.newPassword')"
                  autocomplete="new-password">
								<input v-model="user.confirm_password" type="password" class="form-control mb-20" :placeholder="$t('profile.confirmPassword')">
                <button type="submit" class="btn btn-default btn-lg button-wide-crowd">
                  <span v-if="!processing">{{ $t("saveAll") }}</span>
                  <clip-loader :loading="processing" :color="'#fff'" size="18px"></clip-loader>
                </button>
                <alert :success="alert.success" :error="alert.error"></alert>
							</div>
							<!-- Profile -->
							<!-- Campaigns -->
							<div role="tabpanel" class="tab-pane fade in active" id="profile-campaigns">
                <div v-for="project in projects" :project="project" :key="project.id">
								  <crowdfunding-tile v-if="project.crowdfunding" :project="project" :showStatus="true"></crowdfunding-tile>
                  <fundraising-tile v-else :project="project" :showStatus="true"></fundraising-tile>
							  </div>
              </div>
							<!-- Campaigns -->
							<!-- Contributions -->
							<div role="tabpanel" class="tab-pane fade" id="profile-contributions">
                <p v-for="contribution in contributions" class="contributor">
                  <router-link :to="{ name: 'show_crowdfunding', params: { id: contribution.project.id, title: contribution.project.title }}">
                    <strong>{{ contribution.project.title }}</strong>
                  </router-link>
                  <span class="when">{{ contribution.created_at | daysLeft }} {{ $t('daysAgo') }}</span>
                  <span class="pull-right">
                    <strong>{{ contribution.project.currency | currencySymbol }}{{ contribution.amount }}</strong>
                  </span>
                </p>
							</div>
							<!-- Contributions -->
							<!-- Comments -->
							<div role="tabpanel" class="tab-pane fade" id="profile-comments">
                <div class="scroll-list">
                  <p v-for="(comment, index) in comments" :key="index" class="comment">
                    <router-link :to="{ name: 'show_crowdfunding', params: { id: comment.project.id, title: comment.project.title } }">
                      <div v-if="user.photo.thumb.url" class="avatar" style="height:60px">
                        <img :src="$server_url + user.photo.thumb.url" style="width:60px">
                      </div>
                      <strong>{{ comment.project.title }}</strong>
                    </router-link>
                    <span class="when">{{ comment.created_at | daysLeft }} {{ $t('daysAgo') }}</span>
                    </br>
                    {{ comment.body }}
                  </p>
                </div>
							</div>
							<!-- Comments -->
              <!-- Delivery -->
              <div role="tabpanel" class="tab-pane fade" id="profile-delivery-address">
                <div class="scroll-list">
                  <div class="row">
                    <div class="col-md-12">
                      <delivery-address :countries="countries"></delivery-address>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Organization -->
              <div role="tabpanel" class="tab-pane fade" id="profile-organization">
                <edit-organization :user="user" ></edit-organization>
              </div>
						</div>
						<!-- .tab-content -->
					</div><!-- .col-md-9 -->
					<!-- Socials -->
					<div class="col-md-3">
						<div class="all-categories mb-20">
							<strong>{{ $t("profile.edit.outsideLinks") }}</strong>
						</div>
						<input v-model="user.facebook" class="form-control mb-20" placeholder="Facebook Link">
						<input v-model="user.twitter" class="form-control mb-20" placeholder="Twitter Link">
						<input v-model="user.youtube" class="form-control mb-20" placeholder="YouTube Link">
						<input v-model="user.google" class="form-control mb-20" placeholder="Google Plus Link">
            <input v-model="user.custom_link" class="form-control mb-20" placeholder="Your website link">
					</div><!-- .col-md-3 -->
					<!-- Socials -->
				</div><!-- .row -->
			</div><!-- .container profile-details crowd -->
		</form>

		<!-- STAR BOX -->
		<div class="container mt-50 mb-50">
			<p class="center"><img src="~assets/img/star.png"></p>
		</div>
	</div>

</template>

<script>
// API
import dictionariesApi from '@/api/v1/shared/dictionaries';
import userApi from '@/api/v1/user';
import commentsApi from '@/api/v1/comments';
import contributionsApi from '@/api/v1/contributions';
// Components
import CrowdfundingTile from '@/components/Project/Tiles/Crowdfunding';
import FundraisingTile from '@/components/Project/Tiles/Fundraising';
import ClipLoader from 'vue-spinner/src/ClipLoader';
import UserKyc from '@/components/Profile/Kyc';
import DeliveryAddress from '@/components/Profile/DeliveryAddress';
import Alert from '@/components/Alert';
import FormError from '@/components/FormError';
import UploadBase64 from '@/components/UploadBase64';
import EditOrganization from '@/components/Organization/Edit';
import Datepicker from 'vuejs-datepicker';
import { pl, en } from 'vuejs-datepicker/src/locale';
import Trumbowyg from '@/components/Trumbowyg';
// Models
import userModel from '@/models/user';
// Mixins
import alert from '@/mixins/alert';

export default {
  components: {
    CrowdfundingTile,
    FundraisingTile,
    UserKyc,
    Alert,
    FormError,
    UploadBase64,
    EditOrganization,
    Datepicker,
    Trumbowyg,
    ClipLoader,
    DeliveryAddress,
  },
  mixins: [alert],
  data() {
    return {
      user: userModel.user,
      countries: [],
      professions: [],
      projects: [],
      comments: [],
      contributions: [],
      image: '',
      user_photo: '',
      sizeError: false,
      missingInfo: [],
      locale: localStorage.getItem('lang'),
      processing: false,
    };
  },
  computed: {
    datapickerLang() {
      return this.locale === 'en' ? en : pl;
    },
  },
  methods: {
    submit() {
      this.clearAlerts();
      if (this.user.password && this.user.password.length < 8) {
        this.alert.error = this.$t('profile.passwordTooShort');
      } else if (this.user.password && this.user.password !== this.user.confirm_password) {
        this.alert.error = this.$t('profile.wrongPasswordConfirmation');
      } else {
        this.processing = true;
        const user = Object.assign({}, this.user);
        user.photo = this.user.photo.url;
        userApi
          .updateUser(user)
          .then(() => {
            this.processing = false;

            this.alert.success = this.$t('profile.edit.updateSuccess');
            userApi.myProfile(this.setUser);
          })
          .catch((response) => {
            this.processing = false;

            this.errors = response.response.data;
            this.alert.error = this.$t('profile.incorrectData');
          });
      }
    },
    setUser(user) {
      this.user = user;
      this.user.country_id = this.user.country_id || '';
      this.user.country_of_residence_id =
        this.user.country_of_residence_id || '';
      this.user.profession_id = this.user.profession_id || '';
      this.user_photo = this.user.photo.url
        ? this.$server_url + this.user.photo.url
        : null;
      commentsApi.userComments((user.id), (comments) => {
        this.comments = comments;
      });
      contributionsApi.userContributions((user.id), (contributions) => {
        this.contributions = contributions;
      });

      // Check if user have already submited documents
      if (user.kyc_document_id) { this.documentsSubmitted = true; }
      // Set require information to add document
      if (!this.user.birthday) this.missingInfo.push(this.$t('profile.edit.requiredBirthday'));
      if (!this.user.country_id) this.missingInfo.push(this.$t('profile.edit.requiredCountry'));
      if (!this.user.country_of_residence_id) this.missingInfo.push(this.$t('profile.edit.requiredCountryResidence'));
      return this.missingInfo;
    },
    setDescription(content) {
      this.user.description = content;
    },
    setKycIds(document) {
      this.user.kyc_document_id = document.Id;
      this.user.mangopay_id = document.UserId;
    },
    removeImage() {
      this.image = '';
    },
    getFile(file) {
      if (file.width === file.height) {
        this.sizeError = false;
        this.image = file.base64;
        this.user.photo.url = file.base64;
      } else {
        this.sizeError = true;
      }
    },
    forgotPassword() {
      this.$router.push({ name: 'forgot_password' });
    },
    changeTabByName() {
      const refName = this.$route.query.tab;
      const link = this.$refs[refName];
      link.click();
    },
    changeQueryTab(tab) {
      this.$router.push({ query: { tab } });
    },
  },
  watch: {
    $route() {
      this.changeTabByName();
    },
  },
  mounted() {
    this.changeTabByName();
  },
  created() {
    userApi.myProfile(this.setUser);
    dictionariesApi.getCountries((countries) => {
      this.countries = countries;
    });
    userApi.getProfessions((professions) => {
      this.professions = professions;
    });
    userApi.getProjects((projects) => {
      this.projects = projects;
    });
  },
};
</script>

<style scoped>
  .nav > li > a {
    padding: 10px 10px;
  }
</style>



// WEBPACK FOOTER //
// Edit.vue?5b857090